// DaySummaryService retrieves day detail from static JSON (Refs: R1.1, R1.2, R5.1, G1, G16)
import { logger } from '../../logging/logger'; // G16 structured logging

export interface DayPost {
  id: string;
  url: string;
  title: string;
  text: string;
  publishedAt?: string;
  imageUrls?: string[];
  raw?: { contentSnippet?: string; title?: string; link?: string };
}

export interface DaySentiment {
  postId: string;
  label: 'positive' | 'neutral' | 'negative';
}

export interface DaySummary {
  date: string;
  summary: string;
  sentiments: { positive: number; neutral: number; negative: number };
  topics: string[];
  trendingTopic?: string;
  trendingName?: string;
  selfPraiseCount: number;
  selfPraisePostIds?: string[];
  posts: DayPost[];
  postSentiments: DaySentiment[];
  source: 'static';
  fallbackMessage?: string;
  secondaryHighlights?: Array<{ topic: string; name?: string }>;
}

export interface DaySummaryServiceOptions {
  dataRoot?: string;
  fetchFn?: typeof fetch;
}

function trimTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

function resolveDataRoot(options?: DaySummaryServiceOptions): string {
  // G1: Configurable data root via env or runtime global
  const explicit = options?.dataRoot;
  const envUrl = typeof process !== 'undefined' ? process.env?.STATIC_DATA_URL : undefined;
  const globalUrl = typeof globalThis !== 'undefined' ? (globalThis as any).__STATIC_DATA_URL__ : undefined;
  const candidate = explicit || envUrl || globalUrl || '/data';
  return trimTrailingSlash(candidate);
}

export class DaySummaryService {
  private readonly fetchFn: typeof fetch;
  private readonly dataRoot: string;
  private readonly cache = new Map<string, DaySummary>();

  constructor(options?: DaySummaryServiceOptions) {
    this.fetchFn = options?.fetchFn ?? fetch;
    this.dataRoot = resolveDataRoot(options);
  }

  async getSummary(date: string, options?: { forceRefresh?: boolean }): Promise<DaySummary> {
    if (!options?.forceRefresh && this.cache.has(date)) {
      return this.cache.get(date)!; // R3: reuse cached detail for repeat hovers/clicks
    }

    const year = date.slice(0, 4);
    const month = date.slice(5, 7);
    const url = `${this.dataRoot}/days/${year}/${month}/${date}.json`; // R1.1 static path
    const fetchFn = this.fetchFn; // Preserve native fetch realm binding to avoid "Illegal invocation" in browser
    const resp = await fetchFn(url);

    if (!resp.ok) {
      logger.error('Day summary data request failed', { status: resp.status, statusText: resp.statusText }); // G16
      throw new Error(`Day summary data request failed (${resp.status} ${resp.statusText})`);
    }

    const payload = (await resp.json()) as any;
    const normalizedTopics = Array.isArray(payload.topics)
      ? payload.topics.map((t: any) => (typeof t === 'string' ? t : String(t?.phrase ?? t?.topic ?? ''))).filter(Boolean)
      : [];
    const sentiments = payload.counts ?? payload.sentiments ?? { positive: 0, neutral: 0, negative: 0 };
    const summaryText = payload.summary ?? payload.fallbackMessage ?? 'No summary available';

    const posts = Array.isArray(payload.posts)
      ? payload.posts.map((p: any) => {
          const imageUrls = Array.isArray(p?.imageUrls)
            ? p.imageUrls.filter((url: unknown) => typeof url === 'string' && /^https:\/\//i.test(url))
            : [];
          return {
            id: String(p?.id || p?.url || ''),
            url: String(p?.url || p?.raw?.link || p?.id || ''),
            title: typeof p?.title === 'string' ? p.title : '',
            text: typeof p?.text === 'string' ? p.text : '',
            publishedAt: typeof p?.publishedAt === 'string' ? p.publishedAt : undefined,
            ...(imageUrls.length ? { imageUrls } : {}),
            raw: p?.raw && typeof p.raw === 'object' ? p.raw : undefined,
          };
        }).filter((p: DayPost) => p.id || p.url)
      : [];
    const postSentiments = Array.isArray(payload.sentiments)
      ? payload.sentiments
          .map((s: any) => ({
            postId: String(s?.postId || ''),
            label: s?.label === 'positive' || s?.label === 'negative' || s?.label === 'neutral' ? s.label : undefined,
          }))
          .filter((s: { postId: string; label?: string }): s is DaySentiment => !!s.postId && !!s.label)
      : [];
    const secondaryHighlights = Array.isArray(payload.secondaryHighlights)
      ? payload.secondaryHighlights
          .map((item: any) => {
            const topic = typeof item?.topic === 'string' ? item.topic.trim() : '';
            if (!topic) return undefined;
            const name = typeof item?.name === 'string' && item.name.trim() ? item.name.trim() : undefined;
            return name ? { topic, name } : { topic };
          })
          .filter((item: { topic: string; name?: string } | undefined): item is { topic: string; name?: string } => !!item)
      : [];

    const selfPraisePostIds = Array.isArray(payload.selfPraisePostIds)
      ? payload.selfPraisePostIds.map((id: unknown) => String(id || '').trim()).filter(Boolean)
      : [];

    const normalized: DaySummary = {
      date: payload.date ?? date,
      summary: summaryText,
      sentiments,
      topics: normalizedTopics,
      trendingTopic: typeof payload.trendingTopic === 'string' && payload.trendingTopic.trim() ? payload.trendingTopic.trim() : undefined,
      trendingName: typeof payload.trendingName === 'string' && payload.trendingName.trim() ? payload.trendingName.trim() : undefined,
      selfPraiseCount: Number.isFinite(Number(payload.selfPraiseCount)) ? Math.max(0, Math.trunc(Number(payload.selfPraiseCount))) : 0,
      posts,
      postSentiments,
      source: 'static',
      fallbackMessage: payload.fallbackMessage ?? summaryText,
      ...(secondaryHighlights.length ? { secondaryHighlights } : {}),
      ...(selfPraisePostIds.length ? { selfPraisePostIds } : {}),
    };

    this.cache.set(date, normalized);
    return normalized;
  }
}
